/**
 * EditTenantDialog (TMMPDM-0020) — PATCH /v1/admin/tenants/{slug} with only changed fields.
 * Never sends `kind`. Currency locks for the session on 409 `TENANT_CURRENCY_LOCKED`.
 * 502 `SAAS_AUTH_OUTCOME_UNKNOWN` → re-read, never re-send.
 * TMMPDM-0032: 502 `UPSTREAM_UNAVAILABLE` (tmm-api 0096 — nothing was written) keeps the dialog
 * open with Try again, which re-sends the identical body.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useDiscardChangesGuard, type DiscardChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { SubmitButton, fieldEditable } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isOutcomeUnknown,
  isUpstreamUnavailable,
  isValidationFailed,
  upstreamUnavailableMessage,
  validationFields,
} from '@/api/errors';
import {
  SIGNUP_MODES,
  type SignupMode,
  type TenantPatchRequest,
  type TenantStatus,
  type TenantView,
} from '@/api/tmm/types';

const CURRENCIES = ['USD', 'CRC', 'MXN', 'EUR', 'GBP', 'CAD'] as const;
const STATUSES: TenantStatus[] = ['ACTIVE', 'INACTIVE', 'SUSPENDED'];

interface EditTenantDialogProps {
  open: boolean;
  tenant: TenantView;
  onClose: () => void;
  onUpdated: (tenant: TenantView) => void;
  /** 502 outcome-unknown — parent re-reads; nothing re-PATCHes. */
  onOutcomeUnknown: () => void;
}

export function EditTenantDialog({ open, ...rest }: EditTenantDialogProps) {
  const { t } = useCopy();
  const discard = useDiscardChangesGuard(rest.tenant.name, rest.onClose);
  return (
    <>
      <Dialog open={open} onClose={discard.requestClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('tenants.edit.title', 'Edit tenant')}</DialogTitle>
        {open ? <EditTenantForm key={rest.tenant.slug} {...rest} discard={discard} /> : null}
      </Dialog>
      {discard.dialog}
    </>
  );
}

function EditTenantForm({
  tenant,
  onClose,
  onUpdated,
  onOutcomeUnknown,
  discard,
}: Omit<EditTenantDialogProps, 'open'> & { discard: DiscardChangesGuard }) {
  const { t } = useCopy();
  const [name, setName] = useState(tenant.name);
  const [status, setStatus] = useState<TenantStatus>(tenant.status);
  const [signupMode, setSignupMode] = useState<SignupMode>(tenant.signupMode);
  const [aiEnabledDefault, setAiEnabledDefault] = useState(tenant.aiEnabledDefault);
  const [currency, setCurrency] = useState(tenant.currency);
  const [currencyLocked, setCurrencyLocked] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  /** The body a 502 UPSTREAM_UNAVAILABLE left unwritten — Try again re-sends exactly it. */
  const [retryBody, setRetryBody] = useState<TenantPatchRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currencyEditable = fieldEditable(tenant.editableFields, 'currency');
  const currencies = CURRENCIES.includes(currency as (typeof CURRENCIES)[number])
    ? [...CURRENCIES]
    : [currency, ...CURRENCIES];

  const buildBody = (): TenantPatchRequest | null => {
    const body: TenantPatchRequest = {};
    const trimmed = name.trim();
    if (trimmed !== tenant.name) body.name = trimmed;
    if (status !== tenant.status) body.status = status;
    if (signupMode !== tenant.signupMode) body.signupMode = signupMode;
    if (aiEnabledDefault !== tenant.aiEnabledDefault) body.aiEnabledDefault = aiEnabledDefault;
    if (currencyEditable && currency !== tenant.currency) body.currency = currency;
    return Object.keys(body).length === 0 ? null : body;
  };

  // An editing dialog with more than two fields asks before it drops changes (TMM-B5).
  const dirty = buildBody() !== null;
  const { setDirty } = discard;
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  const submit = async () => {
    const trimmed = name.trim();
    if (trimmed === '' || trimmed.length > 200) {
      setFieldErrors({
        name: t('tenants.edit.nameRequired', 'Name is required (at most 200 characters).'),
      });
      return;
    }
    if (status === 'SUSPENDED' && tenant.status !== 'SUSPENDED' && !confirmSuspend) {
      setConfirmSuspend(true);
      return;
    }
    const body = buildBody();
    if (!body) {
      onClose();
      return;
    }
    await send(body);
  };

  const send = async (body: TenantPatchRequest) => {
    setSubmitting(true);
    setError(null);
    setRetryBody(null);
    setFieldErrors({});
    try {
      const updated = await adminApi.patchTenant(tenant.slug, body);
      onUpdated(updated);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        onOutcomeUnknown();
        return;
      }
      if (isUpstreamUnavailable(err)) {
        setRetryBody(body);
        return;
      }
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        String(err.code) === 'TENANT_CURRENCY_LOCKED'
      ) {
        setCurrencyLocked(true);
        setCurrency(tenant.currency);
        setError(t('tenants.edit.currencyLocked', 'Locked once the tenant has subscriptions'));
        return;
      }
      if (isValidationFailed(err)) {
        setFieldErrors(
          validationFields(err, t, {
            name: t('tenants.edit.nameRequired', 'Name is required (at most 200 characters).'),
          }),
        );
        return;
      }
      setError(errorCopy(err, t, t('tenants.edit.error', 'Could not update the tenant.')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {retryBody !== null ? (
            <Alert
              severity="warning"
              action={
                <SubmitButton
                  variant="text"
                  color="inherit"
                  size="small"
                  busy={submitting}
                  onClick={() => void send(retryBody)}
                >
                  {t('common.tryAgain', 'Try again')}
                </SubmitButton>
              }
            >
              {upstreamUnavailableMessage(t)}
            </Alert>
          ) : null}
          {confirmSuspend ? (
            <Alert severity="warning">
              {t('tenants.edit.suspendConfirm', "Its people can't sign in until it's reactivated.")}
            </Alert>
          ) : null}
          <TextField
            label={t('tenants.edit.nameLabel', 'Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            select
            label={t('tenants.edit.statusLabel', 'Status')}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as TenantStatus);
              setConfirmSuspend(false);
            }}
            fullWidth
            error={Boolean(fieldErrors.status)}
            helperText={fieldErrors.status}
          >
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`tenants.status.${value}`, value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('tenants.edit.signupModeLabel', 'Signup mode')}
            value={signupMode}
            onChange={(e) => setSignupMode(e.target.value as SignupMode)}
            fullWidth
            error={Boolean(fieldErrors.signupMode)}
            helperText={fieldErrors.signupMode}
          >
            {SIGNUP_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {t(`tenants.signupMode.${mode}`, mode)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={aiEnabledDefault}
                onChange={(_e, checked) => setAiEnabledDefault(checked)}
              />
            }
            label={t('tenants.edit.aiDefault', 'AI on by default')}
          />
          {currencyEditable ? (
            <TextField
              select
              label={t('tenants.edit.currencyLabel', 'Currency')}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              fullWidth
              disabled={currencyLocked}
              error={Boolean(fieldErrors.currency)}
              helperText={
                currencyLocked
                  ? t('tenants.edit.currencyLocked', 'Locked once the tenant has subscriptions')
                  : fieldErrors.currency
              }
            >
              {currencies.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={discard.requestClose} disabled={submitting}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void submit()} busy={submitting} disabled={name.trim() === ''}>
          {t('tenants.edit.save', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
